<template>
  <q-dialog
    v-model="shown"
  >
    <q-card
      style="max-width: 100vw; width: 650px;"
    >
      <q-card-section class="row items-center q-mx-xs">
        <span
          class="text-h5"
        >
          Batch Settlements
        </span>
        <q-space />
        <q-btn
          icon="close"
          dense
          flat
          round
          @click="shown = false"
        />
      </q-card-section>
      <q-tabs
        v-model="tab"
        align="justify"
        dense
      >
        <q-tab
          name="configure"
          label="Configure"
        />
        <q-tab
          name="preview"
          label="Preview"
        />
      </q-tabs>
      <q-tab-panels
        v-model="tab"
      >
        <q-tab-panel
          name="configure"
          class="q-pa-none"
        >
          <q-card-section class="row items-center q-pb-none">
            <q-select
              v-model="batchBy"
              class="col-12"
              label="Batch By"
              stack-label
              :options="batchByOptions"
              map-options
              emit-value
              multiple
              use-chips
            >
              <template #selected-item="scope">
                <q-chip
                  :label="scope.opt.label"
                  :tabindex="scope.tabindex"
                  dense
                  :disable="scope.opt.value === 'studio'"
                  :removable="scope.opt.value !== 'studio'"
                  @remove="scope.removeAtIndex(scope.index)"
                />
              </template>
              <template #option="scope">
                <q-item
                  v-bind="scope.itemProps"
                  :disable="scope.opt.value === 'studio'"
                  v-on="scope.itemEvents"
                >
                  <q-item-section>
                    {{ scope.opt.label }}
                  </q-item-section>
                </q-item>
              </template>
            </q-select>
          </q-card-section>
          <q-card-section>
            <span
              class="text-h6 q-mb-md"
            >
              Filters
            </span>
            <div
              class="row q-col-gutter-md"
            >
              <q-select
                v-model="filters.batched"
                class="col-6"
                label="Batched"
                :options="batchedOptions"
                map-options
                emit-value
                dense
                disable
                readonly
              />
              <q-select
                v-model="filters.states"
                class="col-6"
                label="States"
                :options="stateOptions"
                map-options
                emit-value
                use-chips
                multiple
                dense
                disable
                readonly
              />
              <q-select
                v-model="filters.types"
                class="col-6"
                label="Types"
                :options="typeOptions"
                map-options
                emit-value
                use-chips
                multiple
                stack-label
                clearable
              />
              <q-select
                v-model="filters.weeksOf"
                class="col-6"
                label="Weeks Of"
                value-type="Array"
                stack-label
                hide-dropdown-icon
                multiple
                use-chips
                clearable
                @clear="filters.weeksOf = []"
                @click="showWeeksOfCalendar = !showWeeksOfCalendarSelect"
              >
                <template #selected-item="scope">
                  <q-chip
                    :tabindex="scope.tabindex"
                    :label="$d(new Date(scope.opt), 'shortDate')"
                    dense
                    removable
                    @remove="scope.removeAtIndex(scope.index)"
                  />
                </template>
                <template #option="scope">
                  <q-item
                    v-bind="scope.itemProps"
                    v-on="scope.itemEvents"
                  >
                    <q-item-section>
                      <q-item-label>
                        {{ $d(new Date(scope.opt), 'shortDate') }}
                      </q-item-label>
                    </q-item-section>
                  </q-item>
                </template>
                <template #append>
                  <q-icon
                    name="event"
                    class="cursor-pointer"
                  >
                    <q-popup-proxy
                      v-model="showWeeksOfCalendar"
                      transition-show="scale"
                      transition-hide="scale"
                    >
                      <q-date
                        v-model="filters.weeksOf"
                        multiple
                        :options="isValidDate"
                      />
                    </q-popup-proxy>
                  </q-icon>
                </template>
              </q-select>
              <q-select
                v-model="filters.sites"
                class="col-6"
                label="Sites"
                :options="currentUser.authorizedSiteOptions"
                option-label="name"
                map-options
                emit-value
                use-chips
                multiple
                clearable
                :disable="$_.includes(filters.disables, 'sites')"
              />
              <q-select
                v-model="filters.studios"
                class="col-6"
                label="Studios"
                :options="studios"
                option-label="name"
                map-options
                emit-value
                use-chips
                multiple
                clearable
                :use-input="true"
                input-style="search"
                input-debounce="100"
                stack-label
                :disable="$_.includes(filters.disables, 'studios')"
                @filter="filterStudios"
              />
              <q-select
                v-model="filters.movies"
                class="col-6"
                label="Movies"
                :options="movies"
                option-label="name"
                map-options
                emit-value
                use-chips
                multiple
                clearable
                :use-input="true"
                input-style="search"
                input-debounce="100"
                stack-label
                :disable="$_.includes(filters.disables, 'movies')"
                @filter="filterMovies"
              />
              <q-select
                v-model="filters.movieTerms"
                class="col-6"
                label="Movie Terms"
                :options="movieTerms"
                option-label="name"
                map-options
                emit-value
                clearable
                :use-input="true"
                input-style="search"
                input-debounce="100"
                stack-label
                :disable="$_.includes(filters.disables, 'movieTerms')"
                @filter="filterMovieTerms"
              />
            </div>
          </q-card-section>
          <q-card-actions class="justify-end q-mt-md q-mb-xs">
            <q-btn-group>
              <q-btn
                label="Generate"
                color="primary"
                :disable="loading"
                @click="generate"
              />
              <q-btn-dropdown
                auto-close
                color="primary"
                :disable="loading"
                dense
              >
                <q-list>
                  <q-item
                    v-close-popup
                    clickable
                    @click="generatePreview"
                  >
                    <q-item-section>
                      <q-item-label>Preview</q-item-label>
                    </q-item-section>
                  </q-item>
                  <q-item
                    v-close-popup
                    clickable
                    @click="generateSettlements"
                  >
                    <q-item-section>
                      <q-item-label>Settlements</q-item-label>
                    </q-item-section>
                  </q-item>
                </q-list>
              </q-btn-dropdown>
            </q-btn-group>
          </q-card-actions>
        </q-tab-panel>
        <q-tab-panel
          name="preview"
          class="q-pa-none"
        >
          <q-card-section
            class="row items-center q-pb-none"
            style="max-height: 70vh;"
          >
            <data-table
              class="col-12"
              style="max-height: 69vh; overflow-y: scroll;"
              title="Batch Preview"
              :data="previewBatches"
              :columns="[
                { field: 'studio' },
                { field: 'movies', component: 'chip' },
                { label: 'Terms', field: 'term_count', component: 'chip' },
                { label: 'Reports', field: 'bor_count', component: 'chip' },
                { label: 'Settlements', field: 'batch_count', component: 'chip' },
                { label: 'Amount', field: 'batch_amount', component: 'money' }
              ]"
              row-key="index"
              :loading="loading"
            >
              <template #top>
                <div class="full-width text-caption text-right q-pb-none">
                  {{ batchPreviewTitle }}
                </div>
              </template>
            </data-table>
          </q-card-section>
          <q-card-actions class="justify-end q-mt-md q-mb-xs">
            <q-btn
              label="Confirm"
              color="primary"
              :disable="loading"
              @click="generateSettlements"
            />
          </q-card-actions>
        </q-tab-panel>
      </q-tab-panels>
      <q-inner-loading
        :showing="loading"
      />
    </q-card>
  </q-dialog>
</template>

<script>

import { STATES, TYPES } from 'src/models/settlement/BoxOfficeReportSettlement'
import DataTable from 'src/modules/admin/components/model-table/DataTable'

const batchByOptions = ['studio', 'site', 'movie', 'movie_term', 'week_of']

const batchedOptions = [
  { label: 'All', value: null },
  { label: 'Batched', value: 'batched' },
  { label: 'Unbatched', value: 'unbatched' }
]

export default {
  name: 'BoxOfficeReportSettlementBatchDialog',
  components: {
    DataTable
  },
  context: ['currentUser'],
  configuration: ['currentCircuit', 'sites'],
  props: {
    modelValue: {
      type: Boolean,
      required: false,
      default: false
    },
    filters: {
      type: Object,
      required: true
    },
    boxOfficeReportSettlements: {
      type: Object,
      required: true
    },
    studios: {
      type: Array,
      required: true
    },
    movies: {
      type: Array,
      required: true
    },
    movieTerms: {
      type: Array,
      required: true
    }
  },
  emits: ['update:model-value', 'hide'],
  data () {
    return {
      shown: false,
      tab: 'configure',
      batchBy: ['studio'],
      previewBatches: [],
      showWeeksOfCalendar: false,
      showWeeksOfCalendarSelect: false,
      showWeeksOfCalendarSelectTimeout: null,
      loading: false
    }
  },
  computed: {
    batchPreviewTitle () {
      return `${this.previewBatches.length} Batches for ${_.sumBy(this.previewBatches, 'batch_count')} Settlements`
    }
  },
  watch: {
    modelValue () {
      this.shown = this.modelValue
    },
    shown () {
      this.$emit('update:model-value', this.shown)
      if (!this.shown) {
        this.$emit('hide')
      }
    },
    tab () {
      if (this.tab === 'preview') {
        this.generatePreview()
      }
    },
    // When clicking the weeksOf select, it dismisses the calendar if shown (as a click outside the calendar dialog dismisses it)
    // However, we want clicking the select to show the calendar if it is not already shown
    // As clicking the select dismisses a shown calendar, then toggles showWeeksOfCalendar, it quickly sets the value to false then true
    // To avoid this flicker, we need to debounce showWeeksOfCalendar so we can set it to true if it _was_ false, and keep it as false if it _was_ true
    showWeeksOfCalendar () {
      if (!_.isNil(this.showWeeksOfCalendarSelectTimeout)) {
        clearTimeout(this.showWeeksOfCalendarSelectTimeout)
      }

      this.showWeeksOfCalendarSelectTimeout = setTimeout(() => {
        this.showWeeksOfCalendarSelect = this.showWeeksOfCalendar
      }, 100)
    },
    filters: {
      handler () {
        this.previewBatches = []
      },
      deep: true
    }
  },
  created () {
    _.extend(this, {
      batchByOptions: this.$mh.asOptions(batchByOptions),
      batchedOptions,
      stateOptions: _.concat(this.$mh.asOptions(STATES.ALL), { label: 'All', value: null }),
      typeOptions: _.concat(this.$mh.asOptions(TYPES.ALL), { label: 'All', value: null })
    })
  },
  methods: {
    show () {
      this.filters.batched = 'unbatched'
      this.filters.states = ['open']
      this.tab = 'configure'
      this.shown = true
    },
    hide () {
      this.shown = false
    },
    generate () {
      if (this.tab === 'configure') {
        this.tab = 'preview'
      } else {
        this.generateSettlements()
      }
    },
    async generatePreview () {
      this.loading = true

      try {
        const result = await this.boxOfficeReportSettlements.previewBatch(this.$mh.arrayToObject(this.batchBy), {
          site_ids: this.filters.sites?.map(site => site.id),
          movie_term_ids: this.filters.movie_terms?.map(movieTerm => movieTerm.id),
          movie_ids: this.filters.movies?.map(movie => movie.id),
          studio_ids: this.filters.studios?.map(studio => studio.id),
          types: this.filters.types,
          weeks_of: this.filters.weeksOf
        })

        if (!_.isEmpty(result?.errors)) {
          this.$mh.showErrors(result.errors)
          this.previewBatches = []
        } else {
          this.previewBatches = JSON.parse(result?.data?.batches?.value)
        }
      } catch (_error) {
        this.$mh.showErrors('Failed to preview batch Box Office Report Settlements')
      } finally {
        this.loading = false
      }
    },
    async generateSettlements () {
      this.loading = true

      try {
        const result = await this.boxOfficeReportSettlements.batch(this.$mh.arrayToObject(this.batchBy), {
          site_ids: this.filters.sites?.map(site => site.id),
          movie_term_ids: this.filters.movie_terms?.map(movieTerm => movieTerm.id),
          movie_ids: this.filters.movies?.map(movie => movie.id),
          studio_ids: this.filters.studios?.map(studio => studio.id),
          types: this.filters.types,
          weeks_of: this.filters.weeksOf
        })

        if (!_.isEmpty(result?.errors)) {
          this.$mh.showErrors(result.errors)
        } else {
          const batchResults = JSON.parse(result?.data?.batched?.value)
          this.previewBatches = []
          this.$mh.notify(`Batched ${batchResults?.settlements || 0} Box Office Report Settlements into ${batchResults?.studios || 0} Studio Settlements`)
          this.shown = false
        }
      } catch (_error) {
        this.$mh.showErrors('Failed to batch Box Office Report Settlements')
      } finally {
        this.loading = false
      }
    },
    isValidDate (date) {
      return (new Date(date).getDay()) === this.currentCircuit.dayOfWeekStart
    },
    filterStudios (val, update, _abort) {
      update(() => {
        this.filters.studioSearchString = val
      })
    },
    filterMovies (val, update, _abort) {
      update(() => {
        this.filters.movieSearchString = val
      })
    },
    filterMovieTerms (val, update, _abort) {
      update(() => {
        this.filters.movieTermSearchString = val
      })
    }
  }
}
</script>
